import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import EntitlementsService from '../services/EntitlementsService';
import EntityMetadataService from '../services/EntityMetadataService';
import { getReadableEntityIds, type EntityEntitlementsResponse } from '../types/EntityEntitlements';
import type { EntityTypeInfo } from '../hooks/useSchemaEntityTypes';

interface EntityTypesContextType {
    entityTypes: EntityTypeInfo[];
    allEntityTypes: string[];
    isLoading: boolean;
    error: string | null;
    getEntityTypeByName: (name: string) => EntityTypeInfo | undefined;
    refreshEntityTypes: () => Promise<void>;
}

const EntityTypesContext = createContext<EntityTypesContextType | undefined>(undefined);

interface EntityTypesProviderProps {
    children: ReactNode;
}

/** Build EntityTypeInfo from tier1 entitlement item (Id, DisplayName, Description). No schema entity/attribute calls. */
function entityTypesFromEntitlements(payload: EntityEntitlementsResponse | null): EntityTypeInfo[] {
    if (!payload?.EntityEntitlements?.length) return [];
    const readableIds = getReadableEntityIds(payload);
    return payload.EntityEntitlements
        .filter((e) => readableIds.has(e.Id))
        .map((e) => ({
            name: e.Id,
            endpoint: `/api/scim/v2/${e.Id}`,
            schema: `urn:dcps:scim:v1:${e.Id}`,
            description: e.Description
        }));
}

export const EntityTypesProvider: React.FC<EntityTypesProviderProps> = ({ children }) => {
    const [entityTypes, setEntityTypes] = useState<EntityTypeInfo[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadEntityTypes = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const payload = await EntitlementsService.fetchEntityEntitlements();
            const types = entityTypesFromEntitlements(payload ?? null);
            setEntityTypes(types);
        } catch (err) {
            console.error('EntityTypesContext: Error loading entity types from entitlements:', err);
            setError(err instanceof Error ? err.message : 'Failed to load entity types');
            setEntityTypes([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadEntityTypes();
    }, [loadEntityTypes]);

    // When tier1 entitlements change (e.g. on-behalf-of), reload entity types
    useEffect(() => {
        const unsubscribe = EntitlementsService.subscribe(() => {
            const cached = EntitlementsService.getCachedEntityEntitlements();
            setEntityTypes(entityTypesFromEntitlements(cached ?? null));
        });
        return unsubscribe;
    }, []);

    const allEntityTypes = React.useMemo(() => entityTypes.map(t => t.name), [entityTypes]);

    // Sync entity types to EntityMetadataService for getEntityMetadata / categorization
    useEffect(() => {
        EntityMetadataService.getInstance().setResourceTypes(entityTypes);
    }, [entityTypes]);

    const getEntityTypeByName = useCallback(
        (name: string) => entityTypes.find(t => t.name.toLowerCase() === name.toLowerCase()),
        [entityTypes]
    );

    const refreshEntityTypes = useCallback(async () => {
        await EntitlementsService.fetchEntityEntitlements(true);
        const cached = EntitlementsService.getCachedEntityEntitlements();
        setEntityTypes(entityTypesFromEntitlements(cached ?? null));
    }, []);

    const value: EntityTypesContextType = {
        entityTypes,
        allEntityTypes,
        isLoading,
        error,
        getEntityTypeByName,
        refreshEntityTypes
    };

    return (
        <EntityTypesContext.Provider value={value}>
            {children}
        </EntityTypesContext.Provider>
    );
};

export const useEntityTypes = (): EntityTypesContextType => {
    const context = useContext(EntityTypesContext);
    if (context === undefined) {
        throw new Error('useEntityTypes must be used within an EntityTypesProvider');
    }
    return context;
};
